import { Measure } from 'services/model/model.service';
import { createColumnHelper } from '@tanstack/react-table';
import { IntlShape } from 'react-intl';
import { DrillDownTableDateGmvValues } from 'pages/trading/outlier/DrillDownTableDateRangeRow';
import { MeasureFormat } from 'components/measure-unit/MeasureFormat';
import { createMeasureCell, sortComparisonCell } from 'components/table/DataTableUtil';
import {
  formattedMessageCellExporter,
  formattedRegionCellExporter,
  getComparisonCellExporterForMeasureByMetric,
  numberCellExporter,
  percentageCellExporter,
} from 'components/table/DataTableExportUtil';
import { MeasureUnitCode } from 'components/measure-unit/MeasureUnitCode';

const ON_DAY_COMPARISON_GMV = 'onDayComparisonGmv';
const FOUR_WEEK_GMV_AVG = 'fourWeekGmvAvg';
const WEEK_DELTA = 'weekDelta';
const SHARE = 'share';
export const buildDrillDownTableCoreColumns = (intl: IntlShape, measure: Measure) => {
  const columnHelper = createColumnHelper<DrillDownTableDateGmvValues>();
  return [
    columnHelper.accessor(row => row.dayValue, {
      header: () => measure.metric.name,
      id: 'onDayGmv',
      cell: info => <MeasureFormat value={info.getValue()} unit={measure.metric.unit} />,
    }),
    columnHelper.accessor(
      row => ({ displayValue: row.dayDelta, value: row.dayValue, compareValue: row.dayWeekBefore }),
      {
        header: () => intl.formatMessage({ id: 'trading.drilldown.column.delta.day.lastweek' }),
        id: ON_DAY_COMPARISON_GMV,
        cell: info => createMeasureCell(info, measure),
        sortingFn: sortComparisonCell,
      }
    ),
    columnHelper.accessor(
      row => ({ displayValue: row.fourWeekAvgDelta, value: row.dayValue, compareValue: row.fourWeekAvg }),
      {
        header: () => intl.formatMessage({ id: 'trading.drilldown.column.delta.fourWeekAvg' }),
        id: FOUR_WEEK_GMV_AVG,
        cell: info => createMeasureCell(info, measure),
        sortingFn: sortComparisonCell,
      }
    ),
    columnHelper.accessor(
      row => ({ displayValue: row.weekDelta, value: row.weekValue, compareValue: row.weekWeekBefore }),
      {
        header: () => intl.formatMessage({ id: 'trading.drilldown.column.delta.week.lastweek' }),
        id: WEEK_DELTA,
        cell: info => createMeasureCell(info, measure),
        sortingFn: sortComparisonCell,
      }
    ),
  ];
};
export const useDrilldownCoreColumnsCellExporters = () => {
  return {
    ['onDayGmv']: numberCellExporter,
    [ON_DAY_COMPARISON_GMV]: getComparisonCellExporterForMeasureByMetric(MeasureUnitCode.CURRENCY),
    [FOUR_WEEK_GMV_AVG]: getComparisonCellExporterForMeasureByMetric(MeasureUnitCode.CURRENCY),
    [WEEK_DELTA]: getComparisonCellExporterForMeasureByMetric(MeasureUnitCode.CURRENCY),
    [SHARE]: percentageCellExporter,
    ['prdCategory']: (cellValue: unknown, intl: IntlShape) =>
      formattedMessageCellExporter(`product.categories.${cellValue as string}`, intl),
    ['chnCountry']: formattedRegionCellExporter,
  };
};
